import { logError, logInfo } from '../../../utils/logger';
import { moduleConnectionStatus } from '../../../constants/admin';

// Store for registered modules
const registeredModules = new Map();

/**
 * Register a module with the admin panel
 * @param {string} moduleName - Name of the module
 * @param {Object} config - Module configuration
 * @returns {Promise<boolean>} - Success status
 */
export const registerModule = async (moduleName, config) => {
  try {
    if (!moduleName) {
      throw new Error('Module name is required');
    }

    // Add error handling for network connectivity
    if (!navigator.onLine) {
      throw new Error('No network connection available');
    }

    registeredModules.set(moduleName, {
      name: moduleName,
      status: moduleConnectionStatus.ACTIVE,
      config,
      lastChecked: new Date().toISOString()
    });

    logInfo(`Module ${moduleName} registered successfully`);
    return true;
  } catch (error) {
    logError(`Failed to register module ${moduleName}:`, error);
    return false;
  }
};

/**
 * Check the health of a module
 * @param {string} moduleName - Name of the module
 * @returns {Promise<string>} - Module status
 */
export const checkModuleHealth = async (moduleName) => {
  try {
    if (!moduleName) {
      return moduleConnectionStatus.UNKNOWN;
    }

    // Add network connectivity check
    if (!navigator.onLine) {
      return moduleConnectionStatus.ERROR;
    }

    const module = registeredModules.get(moduleName);
    if (!module) {
      return moduleConnectionStatus.INACTIVE;
    }

    // Simulate health check with network awareness
    const isActive = navigator.onLine && Math.random() > 0.2; // 80% chance of being active if online

    // Update module status
    module.status = isActive ? moduleConnectionStatus.ACTIVE : moduleConnectionStatus.INACTIVE;
    module.lastChecked = new Date().toISOString();
    registeredModules.set(moduleName, module);

    return module.status;
  } catch (error) {
    logError(`Failed to check module health for ${moduleName}:`, error);
    return moduleConnectionStatus.ERROR;
  }
};

/**
 * Get module endpoints
 * @param {string} moduleName - Name of the module
 * @returns {Promise<Object|null>} - Module endpoints
 */
export const getModuleEndpoints = async (moduleName) => {
  try {
    if (!moduleName) {
      return null;
    }

    // Add network connectivity check
    if (!navigator.onLine) {
      throw new Error('No network connection available');
    }

    const module = registeredModules.get(moduleName);
    if (!module) {
      return null;
    }

    return module.config || null;
  } catch (error) {
    logError(`Failed to get module endpoints for ${moduleName}:`, error);
    return null;
  }
};

/**
 * Get all registered modules
 * @returns {Array} - List of registered modules
 */
export const getRegisteredModules = () => {
  return Array.from(registeredModules.values());
};

/**
 * Unregister a module
 * @param {string} moduleName - Name of the module
 * @returns {boolean} - Success status
 */
export const unregisterModule = (moduleName) => {
  try {
    if (!moduleName) {
      return false;
    }

    const result = registeredModules.delete(moduleName);
    logInfo(`Module ${moduleName} unregistered: ${result}`);
    return result;
  } catch (error) {
    logError(`Failed to unregister module ${moduleName}:`, error);
    return false;
  }
};